import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, CheckCircle2, Circle, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { CompareButton } from "@/components/DamageInvoice";

function Thumb({ taskId, photo, canDelete, onDeleted }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/cleaning-tasks/${taskId}/photos/${photo.id}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [taskId, photo.id]);
  const del = async () => {
    try { await api.delete(`/cleaning-tasks/${taskId}/photos/${photo.id}`); onDeleted(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-slate-100 shrink-0" data-testid={`checkout-photo-${photo.id}`}>
      {src ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={photo.filename} className="w-full h-full object-cover" /></a>
        : <div className="w-full h-full animate-pulse" />}
      {canDelete && (
        <button onClick={del} data-testid={`checkout-photo-delete-${photo.id}`}
          className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-slate-500 hover:text-red-600 transition-colors"><Trash2 className="w-3 h-3" /></button>
      )}
    </div>
  );
}

function RoomRow({ room, idx, photos, task, user, canUpload, onChanged }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const isCleaner = user?.role === "cleaner";
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    if (room) fd.append("room", room);
    try { await api.post(`/cleaning-tasks/${task.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const done = photos.length > 0;
  return (
    <div className="flex items-center gap-3 py-3 border-t border-slate-100" data-testid={`checkout-room-${idx}`}>
      {room ? (done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />) : <span className="w-5" />}
      <div className="w-32 shrink-0 text-sm">{room || "Other photos"}</div>
      <div className="flex-1 flex gap-2 overflow-x-auto">
        {photos.map((p) => <Thumb key={p.id} taskId={task.id} photo={p} onDeleted={onChanged}
          canDelete={!isCleaner || (p.uploaded_by_id === user?.id && task.status !== "done")} />)}
      </div>
      {canUpload && room && (
        <>
          <input ref={input} type="file" accept="image/*" multiple capture="environment" className="hidden"
            data-testid={`checkout-room-input-${idx}`} onChange={(e) => upload(e.target.files)} />
          <Button size="sm" variant={done ? "outline" : "default"} className={`rounded-full shrink-0 ${done ? "" : "ns-btn-primary"}`} disabled={busy}
            onClick={() => input.current?.click()} data-testid={`checkout-room-add-${idx}`}>
            {busy ? <Upload className="w-3.5 h-3.5 animate-pulse" /> : <Camera className="w-3.5 h-3.5" />}
          </Button>
        </>
      )}
    </div>
  );
}

export function CheckoutPhotosDialog({ task, user, onClose, onReady }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/cleaning-tasks/${task.id}/photos`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id]);
  const isCleaner = user?.role === "cleaner";
  const canUpload = task.status !== "done" || !isCleaner;
  const loose = data ? data.photos.filter((p) => !data.rooms.includes(p.room)) : [];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="checkout-photos-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif">Check-out photos · {task.property_name}</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-slate-400">Loading…</div> : (
          <>
            <div className={`text-sm mb-1 ${data.ready ? "text-emerald-700" : "text-amber-700"}`} data-testid="checkout-photos-count">
              {data.covered} / {data.rooms.length} rooms photographed {data.ready ? "— ready to mark Done" : `— still to do: ${data.missing_rooms.join(", ")}`}
            </div>
            {!isCleaner && <div className="mb-2"><CompareButton taskId={task.id} title={task.property_name} testId="checkout-photos-compare" /></div>}
            <div data-testid="checkout-photos-grid">
              {data.rooms.map((room, i) => (
                <RoomRow key={room} room={room} idx={i} task={task} user={user} canUpload={canUpload} onChanged={load}
                  photos={data.photos.filter((p) => p.room === room)} />
              ))}
              {loose.length > 0 && <RoomRow room="" idx="other" task={task} user={user} canUpload={false} onChanged={load} photos={loose} />}
            </div>
            <div className="text-xs text-slate-400 mt-2">Photos are kept for 90 days.</div>
            {isCleaner && task.status !== "done" && (
              <Button className="w-full mt-4 rounded-full ns-btn-primary" disabled={!data.ready} onClick={onReady} data-testid="checkout-photos-mark-done">
                Mark turnover Done
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
